import { Box, Icon, IconButton, Image, useDisclosure } from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useEffect, useRef } from "react";
import { TbView360Number } from "react-icons/tb";
import { PannellumViewer } from "../../../../../shared/models/PanoramicPhoto";
import { useStores } from "../../../../../shared/stores/RootStore";
import { NodePhotoModal } from "../../../../../shared/views/NodePhotoModal/NodePhotoModal";
import { ViewIcon } from "../../../../../shared/views/icons";
import {
  getNodeConfig,
  saveNodePosition,
} from "../../../../../shared/utils/panoramic-utils";

const SelectedNodeImageBase = () => {
  const { craNodeStore } = useStores();
  const nodePhotoModal = useDisclosure();
  const node = craNodeStore.getSelected()!;
  const src = node.photo?.url || "";
  const isPanoramic = !!node.photo?.panoramic;
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<PannellumViewer | null>(null);

  useEffect(() => {
    if (!isPanoramic || !src || !containerRef.current) return;
    let blobUrl: string | null = null;

    fetch(src)
      .then((res) => res.blob())
      .then((blob) => {
        blobUrl = URL.createObjectURL(blob);

        saveNodePosition(viewerRef.current);
        viewerRef.current?.destroy();
        viewerRef.current = null;

        const viewer = window.pannellum.viewer(
          containerRef.current!,
          getNodeConfig({
            type: "equirectangular",
            panorama: blobUrl,
            autoLoad: true,
            showZoomCtrl: false,
            showFullscreenCtrl: false,
            mouseZoom: true,
            draggable: true,
          }),
        );

        viewerRef.current = viewer;

        const syncView = () => saveNodePosition(viewer);
        viewer.on("mouseup", syncView);
        viewer.on("touchend", syncView);
        viewer.on("zoomchange", syncView);
      })
      .catch((err) => console.error("fetch failed", err));

    return () => {
      saveNodePosition(viewerRef.current);
      viewerRef.current?.destroy();
      viewerRef.current = null;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [isPanoramic, src, node.id]);

  return (
    <Box position="relative" w="100%" h="100%">
      {isPanoramic ? (
        <Box
          ref={containerRef}
          w="100%"
          h="100%"
          bg="black"
          borderRadius="md"
          overflow="hidden"
        />
      ) : (
        <Box position="relative" overflow="hidden" w="100%" h="100%">
          <Image src={src} objectFit="contain" w="100%" h="100%" />
        </Box>
      )}
      <Box
        position="absolute"
        right={2}
        top={2}
        zIndex={10}
        display="flex"
        alignItems="center"
        gap={2}
      >
        {isPanoramic && (
          <TbView360Number
            size="2rem"
            color="white"
            style={{ opacity: 0.7, pointerEvents: "none" }}
          />
        )}
        <IconButton
          colorPalette="primary"
          onClick={nodePhotoModal.onOpen}
          aria-label="Open"
        >
          <Icon as={ViewIcon} boxSize="1.5rem" />
        </IconButton>
      </Box>
      {nodePhotoModal.open && (
        <NodePhotoModal onClose={nodePhotoModal.onClose} />
      )}
    </Box>
  );
};

export const SelectedNodeImage = observer(SelectedNodeImageBase);
